import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import WBCDataTable from "../data-table/WBCDataTable";
import TeamCommentsModal from "./TeamCommentsModal";
import Theme from "../../Theme";

const CommentIcon = styled.div`
  cursor: pointer;
  position: relative;

  &:hover {
    opacity: 0.8;
  }
`;

const dateFormatter = cell => {
  const value = cell?.value;

  if (value === undefined || value === null || value === "") {
    return <div style={{ fontWeight: "bold" }}>-</div>;
  }

  return <div style={{ fontWeight: "bold" }}>{value}</div>;
};

const RosterSubmissionsTable = ({ data, intListMax, provisionalMax, finalRosterMax, onUpdateComments }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const handleOpenModal = useCallback(team => {
    setSelectedTeam(team);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedTeam(null);
  }, []);

  const handleSaveComment = useCallback(
    (teamId, comments) => {
      if (onUpdateComments) {
        onUpdateComments(teamId, comments);
      }
      handleCloseModal();
    },
    [onUpdateComments, handleCloseModal]
  );

  const commentsFormatter = cell => {
    const team = cell?.row;
    const hasComments = team?.comments && team.comments.trim() !== "";
    const color = hasComments ? Theme.wbcBlue : Theme.grey;

    return (
      <CommentIcon onClick={() => handleOpenModal(team)}>
        <i className="fas fa-comment" style={{ color }}></i>
      </CommentIcon>
    );
  };

  const intListTotalFormatter = cell => {
    const value = cell?.row?.intListTotal;

    if (value === undefined || value === null) {
      return (
        <div
          style={{
            fontWeight: "bold"
          }}
        >
          -
        </div>
      );
    }

    const color = value === intListMax ? Theme.wbcGreen : "inherit";

    return (
      <div
        style={{
          fontWeight: "bold",
          color
        }}
      >
        {value}/{intListMax}
      </div>
    );
  };
  const provListTotalFormatter = cell => {
    const value = cell?.row?.provListTotal;

    if (value === undefined || value === null) {
      return (
        <div
          style={{
            fontWeight: "bold"
          }}
        >
          -
        </div>
      );
    }

    const color = value === provisionalMax ? Theme.wbcGreen : "inherit";

    return (
      <div
        style={{
          fontWeight: "bold",
          color
        }}
      >
        {value}/{provisionalMax}
      </div>
    );
  };

  const finalRosterTotalFormatter = cell => {
    const value = cell?.row?.finalRosterTotal;

    if (value === undefined || value === null) {
      return (
        <div
          style={{
            fontWeight: "bold"
          }}
        >
          -
        </div>
      );
    }

    const color = value === finalRosterMax ? Theme.wbcGreen : "inherit";

    return (
      <div
        style={{
          fontWeight: "bold",
          color
        }}
      >
        {value}/{finalRosterMax}
      </div>
    );
  };

  const columns = [
    {
      name: "",
      key: "comments",
      sortable: false,
      width: 50,
      formatter: commentsFormatter
    },
    {
      name: "Federation",
      key: "fedTeamName",
      width: 250
    },
    {
      name: "Interest List",
      key: "intListTotal",
      width: 150,
      sortable: true,
      formatter: intListTotalFormatter
    },
    {
      name: "Int. Finalized",
      key: "intListFinalizedDate",
      width: 200,
      sortable: true,
      formatter: dateFormatter
    },
    {
      name: "Provisional",
      key: "provListTotal",
      width: 200,
      sortable: true,
      formatter: provListTotalFormatter
    },
    {
      name: "Prov. Finalized",
      key: "provListFinalizedDate",
      width: 200,
      formatter: dateFormatter
    },
    {
      name: "Final Roster",
      key: "finalRosterTotal",
      width: 200,
      sortable: true,
      formatter: finalRosterTotalFormatter
    },
    {
      name: "Fin. Finalized",
      key: "finalRosterFinalizedDate",
      width: 200,
      formatter: dateFormatter
    }
  ];

  return (
    <div>
      <WBCDataTable id="Roster Submissions Table" columns={columns} data={data} useBCR />

      <TeamCommentsModal isOpen={showModal} team={selectedTeam} onClose={handleCloseModal} onSave={handleSaveComment} />
    </div>
  );
};

RosterSubmissionsTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      comments: PropTypes.string,
      fedTeamName: PropTypes.string,
      intListTotal: PropTypes.number,
      intListFinalizedDate: PropTypes.string,
      provListTotal: PropTypes.number,
      provListFinalizedDate: PropTypes.string,
      finalRosterTotal: PropTypes.number,
      finalRosterFinalizedDate: PropTypes.string
    })
  ).isRequired,
  intListMax: PropTypes.number.isRequired,
  provisionalMax: PropTypes.number.isRequired,
  finalRosterMax: PropTypes.number.isRequired,
  onUpdateComments: PropTypes.func
};

export default RosterSubmissionsTable;
